<template>
  <BaseAccordion class="bg-gray-200 rounded mb-2">
    <div class="p-2">{{ value.degreeMapName || 'Untitled Degree Map' }}</div>
    <template #content>
      <section class="px-4 py-2 bg-white border-l border-r border-b rounded">
        <div class="px-1">
          <div
            v-if="isFieldVisible(fields.degreeMapName)"
            class="mb-2"
          >
            <strong>{{ fields.degreeMapName.label }}</strong>
            <div>{{ value.degreeMapName || '-' }}</div>
          </div>
          <div class="flex">
            <div
              v-if="isFieldVisible(fields.totalDegreeMapCredits)"
              class="flex-1 mr-2"
            >
              <strong>{{ fields.totalDegreeMapCredits.label }}</strong>
              <div>{{ totalActualCredits || '-' }}</div>
            </div>
            <div
              v-if="isFieldVisible(fields.degreeMapEffectiveCatalogYear)"
              class="flex-1 ml-2"
            >
              <strong>{{ fields.degreeMapEffectiveCatalogYear.label }}</strong>
              <div>{{ `${getTermDisplayName(documentValue.startTerm || {})} - ${getTermDisplayName(documentValue.endTerm || {})}` }}</div>
            </div>
          </div>
          <div v-if="isFieldVisible(fields.degreeMapNarrative)">
            <strong>{{ fields.degreeMapNarrative.label }}</strong>
            <div v-html="value.degreeMapNarrative || '-'" />
          </div>
        </div>
        <hr />
        <div
          v-for="(semester, semesterIndex) in value.semesters || []"
          :key="`semester-${semesterIndex}`"
        >
          <div
            class="flex mb-2"
            data-test="degreemap-semester-stats"
          >
            <div
              v-if="isFieldVisible(fields.year)"
              class="flex-1 bg-white p-2 text-center border rounded mr-1"
            >
              <strong>{{ fields.year.label }}</strong>
              <div>{{ semester.year || '-' }}</div>
            </div>
            <div
              v-if="isFieldVisible(fields.semester)"
              class="flex-1 bg-white p-2 text-center border rounded mr-1 ml-1"
            >
              <strong>{{ fields.semester.label }}</strong>
              <div>{{ semester.semester || '-' }}</div>
            </div>
            <div
              v-if="isFieldVisible(fields.actualCredits)"
              class="flex-1 bg-white p-2 text-center border rounded mr-1 ml-1"
            >
              <strong>{{ fields.actualCredits.label }}</strong>
              <div>{{ getTotalActualCreditsForSemester(semesterIndex) || 0 }}</div>
            </div>
            <div
              v-if="isFieldVisible(fields.progressCredits)"
              class="flex-1 bg-white p-2 text-center border rounded ml-1"
            >
              <strong>{{ fields.progressCredits.label }}</strong>
              <div>{{ getTotalProgressCreditsForSemester(semesterIndex) || 0 }}</div>
            </div>
          </div>
          <section
            v-for="(requirement,index) in filterRequirements(semester.requirements) || []"
            :key="index"
            class="border rounded mb-2"
            data-test="degreemap-requirement"
          >
            <div
              v-if="isFieldVisible(fields.requirementSelect)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.requirementSelect.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                <ul>
                  <li
                    v-for="(rule, ruleIndex) in requirement.requirementSelect"
                    :key="ruleIndex"
                  >
                    <div v-if="rule.type === 'course' || rule.type === 'courses'" :class="ruleIndex !== 0 && 'left-margin'" v-html="getRequirementDisplayValue(rule.value)"/>
                    <template v-else-if="rule.type === 'courseSets'">{{ getCourseSetNameById(rule.value) }} ({{ capitalize($localize('course set')) }})</template>
                    <div v-else :class="ruleIndex !== 0 && 'left-margin'">{{ rule.value }} ({{ decamelize(rule.type) }})</div>
                    <strong v-if="ruleIndex < (requirement.requirementSelect.length - 1)" style="position: absolute">OR</strong>
                  </li>
                </ul>
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.courseRequirementGroup)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.courseRequirementGroup.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ getRequirementGroups(requirement, requirementGroups) }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.minimumGrade)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.minimumGrade.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ requirement.minimumGrade || '-' }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.courseRequirementGroupFreeText)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.courseRequirementGroupFreeText.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ requirement.courseRequirementGroupFreeText || '-' }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.area)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.area.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ (disabledDecamelizeField? requirement.area : decamelize(requirement.area)) || '-' }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.actualCredits)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.actualCredits.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ outputCreditRange(getRequirementCreditRange(getRequirementCreditValues(requirement,'creditHours','actualCredits'))) || '-' }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.progressCredits)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.progressCredits.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ outputCreditRange(getRequirementCreditRange(getRequirementCreditValues(requirement,'academicProgressHours','progressCredits'))) || '-' }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.contactHours)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.contactHours.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ outputCreditRange(getRequirementCreditRange(getRequirementCreditValues(requirement,'contactHours','contactHours'))) || '-' }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.criticality)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.criticality.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ requirement.criticality?'Yes':'No' }}
              </div>
            </div>
            <div
              v-if="isFieldVisible(fields.clinical)"
              class="flex"
            >
              <div class="flex-1 px-3 py-2 border-b">{{ fields.clinical.label }}</div>
              <div class="flex-1 px-3 py-2 border-b">
                {{ requirement.clinical || '-' }}
              </div>
            </div>
          </section>
        </div>
        <div data-test="degreemap-total-stats">
          <div class="flex mb-2">
            <div v-if="isFieldVisible(fields.totalGeneralEducationCredits)" class="mr-2 flex-1">
              <strong>{{ fields.totalGeneralEducationCredits.label }}</strong>
              <div>{{ totalGeneralEducationCredits }}</div>
            </div>
            <div v-if="isFieldVisible(fields.totalMajorCredits)" class="ml-2 flex-1">
              <strong>{{ fields.totalMajorCredits.label }}</strong>
              <div>{{ totalMajorCredits }}</div>
            </div>
          </div>
          <div class="flex">
            <div v-if="isFieldVisible(fields.totalMinorCredits)" class="mr-2 flex-1">
              <strong>{{ fields.totalMinorCredits.label }}</strong>
              <div>{{ totalMinorCredits }}</div>
            </div>
            <div v-if="isFieldVisible(fields.totalElectiveCredits)" class="ml-2 flex-1">
              <strong>{{ fields.totalElectiveCredits.label }}</strong>
              <div>{{ totalElectiveCredits }}</div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </BaseAccordion>
</template>

<script>
import _minBy from 'lodash/minBy'
import _maxBy from 'lodash/maxBy'
import _isObject from 'lodash/isObject'
import _isNumber from 'lodash/isNumber'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { DISABLE_DECAMELIZE_FIELDS } from '@/constants/featureFlags'
import { decamelize, capitalize } from '@/utils/generalUtils'
import { courseNameWithFallbacks } from '@/utils/entityNames'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    documentValue: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    courses: {
      type: Array,
      default: () => [],
    },
    courseSets: {
      type: Array,
      default: () => [],
    },
    requirementGroups: {
      type: Array,
      default: () => [],
    },
    terms: {
      type: Array,
      default: () => [],
    },
    semesterNames: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { disabledDecamelizeField } = useFeatureFlags({
      disabledDecamelizeField: DISABLE_DECAMELIZE_FIELDS,
    })

    return { disabledDecamelizeField }
  },
  computed: {
    fields() {
      return this.config.fields
    },
    allRequirements() {
      return this.value.semesters.reduce((acc, sem) => {
        acc.push(...sem.requirements)
        return acc
      }, [])
    },
    totalGeneralEducationCredits() {
      return this.getTotalCreditsOutput(
        this.allRequirements.filter((r) => r.area === 'generalEducation'),
        'creditHours',
        'actualCredits'
      )
    },
    totalMajorCredits() {
      return this.getTotalCreditsOutput(
        this.allRequirements.filter((r) => r.area === 'major'),
        'creditHours',
        'actualCredits'
      )
    },
    totalMinorCredits() {
      return this.getTotalCreditsOutput(
        this.allRequirements.filter((r) => r.area === 'minor'),
        'creditHours',
        'actualCredits'
      )
    },
    totalElectiveCredits() {
      return this.getTotalCreditsOutput(
        this.allRequirements.filter((r) => r.area === 'elective'),
        'creditHours',
        'actualCredits'
      )
    },
    totalActualCredits() {
      return this.getTotalCreditsOutput(
        this.allRequirements,
        'creditHours',
        'actualCredits'
      )
    },
  },
  methods: {
    decamelize,
    capitalize,
    filterRequirements(requirements) {
      return requirements.filter(
        (requirement) =>
          requirement.requirementSelect &&
          Array.isArray(requirement.requirementSelect)
      )
    },
    courseByGroupId(courseGroupId) {
      return (
        this.courses.find(
          (course) =>
            course.courseGroupId === courseGroupId ||
            course.deprecatedCourseGroupId === courseGroupId
        ) || {}
      )
    },
    getCourseSetByCourseGroupId(courseSetGroupId) {
      return this.courseSets.find((s) => s.courseSetGroupId === courseSetGroupId)
    },
    getCourseSetNameById(id) {
      return this.getCourseSetByCourseGroupId(id)?.name
    },
    getTermDisplayName(term = {}) {
      if (!term.displayName) {
        term = this.terms.find((el) => el.id === term.id) || term
      }
      return (
        term.displayName ||
        (term.semester && term.year
          ? [this.semesterNames[term.semester], term.year].join(' ')
          : '')
      )
    },
    isFieldVisible(field) {
      return field && field?.hidden !== true // NOTE: Do we have catalog specifiec individual field settings for degreeMaps?
    },
    normalizeMinMax({ min, max, value }) {
      if (_isNumber(value)) return { min: value, max: value }
      if (!_isNumber(min) && _isNumber(max)) return { min: max, max }
      if (!_isNumber(max) && _isNumber(min)) return { max: min, min }
      return { min, max }
    },
    getCourseName(courseId) {
      const course = this.courseByGroupId(courseId)
      return Object.keys(course).length
        ? `${course.subjectCode}${
            course.courseNumber
          } ${courseNameWithFallbacks(course)}`
        : ''
    },
    getRequirementDisplayValue(values) {
      if (Array.isArray(values)) {
        return values
          .map((v) => this.getCourseName(v))
          .join('<br/><strong>AND</strong> ')
      }
      return this.getCourseName(values)
    },
    getRequirementGroups(requirement, resources) {
      const requirementGroups = requirement.requirementSelect.reduce(
        (acc, item) => {
          if (item.type === 'course' || item.type === 'courses') {
            const { requirementGroup = null } = this.courseByGroupId(item.value)
            acc.push(requirementGroup)
          } else if (item.type === 'courseSets') {
            const { requirementGroups = [] } = this.getCourseSetByCourseGroupId(item.value)
            acc.push(...requirementGroups)
          }
          return acc
        },
        []
      )
      return (
        Array.from(new Set(requirementGroups))
          .map((id) => {
            const res = resources.find((r) => r.id === id) || {}
            return res.displayName || id
          })
          .join(', ') || null
      )
    },
    getRequirementCreditRange(creditValues) {
      let creditRange = {}
      if (Array.isArray(creditValues)) {
        const { min } =
          _minBy(creditValues.filter((v) => _isNumber(v.min)), (c) => c.min) ||
          {}
        const { max } =
          _maxBy(creditValues.filter((v) => _isNumber(v.max)), (c) => c.max) ||
          {}
        Object.assign(creditRange, { min, max })
      } else {
        creditRange = creditValues
      }
      return creditRange
    },
    getTotalCreditsOutput(requirements, creditType, creditKey) {
      const totalCredits = this.filterRequirements(requirements).reduce(
        (acc, r) => {
          const creditValues = this.getRequirementCreditValues(
            r,
            creditType,
            creditKey
          )
          const creditRange = this.getRequirementCreditRange(creditValues)
          acc.min += creditRange.min || 0
          acc.max += creditRange.max || 0
          return acc
        },
        { min: 0, max: 0 }
      )
      return this.outputCreditRange(totalCredits)
    },
    outputCreditRange({ min, max }) {
      return min === max
        ? min
        : [min, max].filter((v) => _isNumber(v)).join(' - ') || ''
    },
    getCourseCreditMinMaxValue(courseId, creditType) {
      const course = this.courseByGroupId(courseId)
      const { min, max, value } =
        (course && course.credits && course.credits[creditType]) || {}
      return this.normalizeMinMax({ min, max, value })
    },
    getSingleOrMultipleCoursesCreditMinMaxValue(courseIds, creditType) {
      if (Array.isArray(courseIds)) {
        const min = courseIds
          .map(
            (c) =>
              this.normalizeMinMax(
                this.getCourseCreditMinMaxValue(c, creditType)
              ).min
          )
          .reduce((a, b) => a + (b || 0), 0)
        const max = courseIds
          .map(
            (c) =>
              this.normalizeMinMax(
                this.getCourseCreditMinMaxValue(c, creditType)
              ).max
          )
          .reduce((a, b) => a + (b || 0), 0)
        return { min, max }
      }
      return this.getCourseCreditMinMaxValue(courseIds, creditType)
    },
    getRequirementCreditValues(requirement, creditType, creditKey) {
      if (_isObject(requirement[creditKey]))
        return this.normalizeMinMax(requirement[creditKey])
      const creditRange = {}
      requirement.requirementSelect.forEach((item) => {
        if (['course', 'courses', 'courseSets'].includes(item.type)) {
          if (item.type === 'courseSets') {
            const resource = this.getCourseSetByCourseGroupId(item.value)
            const courseSetCredits = resource?.credits?.[creditType] || {}
            const actualCredits = resource?.actualCredits
            const { min, max, value } = { ...courseSetCredits, ...actualCredits }
            const normalizeMinMax = this.normalizeMinMax({ min, max, value })
            if (_isNumber(normalizeMinMax.min)) {
              creditRange.min = Math.min(
                creditRange.min || Number.POSITIVE_INFINITY,
                normalizeMinMax.min
              )
            }
            if (_isNumber(normalizeMinMax.max)) {
              creditRange.max = Math.max(
                creditRange.max || Number.NEGATIVE_INFINITY,
                normalizeMinMax.max
              )
            }
          } else {
            const {
              min,
              max,
            } = this.getSingleOrMultipleCoursesCreditMinMaxValue(
              item.value,
              creditType
            )
            creditRange.min = Math.min(
              creditRange.min || Number.POSITIVE_INFINITY,
              min
            )
            creditRange.max = Math.max(
              creditRange.max || Number.NEGATIVE_INFINITY,
              max
            )
          }
        }
      })
      return creditRange
    },
    getTotalActualCreditsForSemester(semesterIndex) {
      return this.getTotalCreditsOutput(
        this.value.semesters[semesterIndex].requirements,
        'creditHours',
        'actualCredits'
      )
    },
    getTotalProgressCreditsForSemester(semesterIndex) {
      return this.getTotalCreditsOutput(
        this.value.semesters[semesterIndex].requirements,
        'academicProgressHours',
        'progressCredits'
      )
    },
  },
}
</script>

<style scoped>
.flex-1 {
  flex: 1;
}
.left-margin {
  margin-left: 1.8em;
}
</style>
